import React, { useEffect, useRef, useState } from "react";
import "./styles/iframe-widget.css";
import IntentConfirmationPopup from "./IntentConfirm";

const API_URL = process.env.REACT_APP_API_URL || "https://askhr.sltdigitallab.lk/api";
const CHAT_URL = "https://askhr.sltdigitallab.lk/api/v1/chat";
const CONFIRM_URL = "https://askhr.sltdigitallab.lk/api/v1/chat/confirm";

// const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5003/api";
// const CHAT_URL = "http://localhost:5003/api/v1/chat";
// const CONFIRM_URL = "http://localhost:5003/api/v1/chat/confirm";

const IframeWidget = () => {
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  
  const [confirmationData, setConfirmationData] = useState(null);
  const [pendingQuery, setPendingQuery] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const [chatId] = useState(
    () => (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
      `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  );

  // Auth Check
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/welcome`, { credentials: "include" });
        if (!res.ok) throw new Error("Auth check failed");
        const data = await res.json();
        setUser(data?.authenticated ? data : null);
      } catch {
        setUser(null);
      } finally {
        setCheckingAuth(false);
      }
    })();
  }, []);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setSending(true);

    try {
      const res = await fetch(CHAT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ query: text, chat_id: chatId }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data?.intent_confirmation?.needs_confirmation === true) {
          setConfirmationData(data.intent_confirmation);
          setPendingQuery(text);
          return;
        }

        if (data?.answer) {
          setMessages((m) => [...m, { role: "assistant", text: data.answer }]);
        }
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setSending(false);
    }
  };

  const handleConfirmation = async (selectedOption) => {
    setConfirmationData(null);
    const queryToSend = pendingQuery;
    setPendingQuery(null);
    setSending(true);

    try {
      const res = await fetch(CONFIRM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          chat_id: chatId,
          original_query: queryToSend,
          selected_option: selectedOption,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data?.answer) {
          setMessages((m) => [...m, { role: "assistant", text: data.answer }]);
        }
      }
    } catch (error) {
      console.error("Confirmation error:", error);
    } finally {
      setSending(false);
    }
  };

  const handleFeedbackClick = async (index, value) => {
    const target = messages[index];
    const isRemoving = target.feedback === value;

    setMessages(prev => prev.map((mm, idx) => 
      idx === index ? { ...mm, feedback: isRemoving ? null : value, _hover: false } : mm
    ));

    let userMsg = "";
    for (let k = index - 1; k >= 0; k--) {
      if (messages[k].role === "user") {
        userMsg = messages[k].text;
        break;
      }
    }

    await fetch(`${API_URL}/v1/feedback`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        chat_id: chatId,
        user_msg: userMsg,
        assistant_msg: target.text,
        reaction: isRemoving ? null : value,
        user_id: user?.email || null,
      }),
    });
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="page widget-mode">
      <main className="stack-compact">
        <section className="chatCard">
          <div className="chatShell">
            {!user && !checkingAuth ? (
              <div className="state">
                <span>Please log in to continue</span>
                <button className="login-btn" style={{marginTop: '15px'}} onClick={() => window.open(`${API_URL}/login`, '_blank')}>
                  Login with Microsoft
                </button>
              </div>
            ) : user && (
              <div className="chatLayout">
		<IntentConfirmationPopup 
                  confirmationData={confirmationData} 
                  onConfirm={handleConfirmation} 
                  onCancel={() => setConfirmationData(null)} 
                  isSubmitting={isConfirming} 
                />
                <div className="messages">
                  {messages.map((m, i) => {
                    const formatted = m.role === "assistant" 
                      ? m.text.replace(/\*\*/g, "").replace(/\*/g, "•").replace(/\n/g, "<br/>") 
                      : m.text;

                    return (
                      <div key={i} className={`messageRow ${m.role === "user" ? "alignRight" : "alignLeft"}`}
                           onMouseEnter={() => m.role === "assistant" && setMessages(prev => prev.map((mm, idx) => idx === i ? {...mm, _hover: true} : mm))}
                           onMouseLeave={() => m.role === "assistant" && setMessages(prev => prev.map((mm, idx) => idx === i ? {...mm, _hover: false} : mm))}>
                        
                        {m.role === "assistant" && <div className="labelBubble">HR</div>}
                        
                        <div className={`bubble ${m.role === "assistant" ? "fromBot" : "fromUser"} bubble-anchor`}>
                          <div dangerouslySetInnerHTML={{ __html: formatted }} />
                          
                          {/* Feedback UI */}
                          {m.role === "assistant" && m._hover && !m.feedback && (
                            <div className="hover-reactions">
                              <span onClick={() => handleFeedbackClick(i, "up")}>👍</span>
                              <span onClick={() => handleFeedbackClick(i, "down")}>👎</span>
                            </div>
                          )}
                          {m.feedback && (
                            <div className="selected-reaction" onClick={() => handleFeedbackClick(i, m.feedback)}>
                              {m.feedback === "up" ? "👍" : "👎"}
                            </div>
                          )}
                        </div>
                        
                        {m.role === "user" && <div className="labelBubble">ME</div>}
                      </div>
                    );
                  })}
                </div>
                  

                    <div className="composer">
                    {sending ? (
                        <div className="wrapper">
                        <div className="loading-dots">
                            <span></span>
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                        {/* <h6>Processing&hellip;</h6> */}
                        </div>
                    ) : (
                        <div className="composerMain">
                        <textarea 
                            className="composerInput" 
                            value={input} 
                            onChange={(e) => setInput(e.target.value)} 
                            onKeyDown={onKeyDown} 
                            placeholder="Type a message..." 
                            rows={1} 
                        />
                        <button 
                            className="sendButton" 
                            onClick={sendMessage} 
                            disabled={!input.trim()}
                        >
                            ➤
                        </button>
                        </div>
                    )}
                    </div>
              </div>
            )}
          </div>
        </section>
      </main>

    </div>
  );
};

export default IframeWidget;
